import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import AuthForm from './components/AuthForm';
import TodoBoard from './components/TodoBoard';
import Dashboard from './components/Dashboard';
import AdminPanel from './components/AdminPanel';
import CalendarView from './components/CalendarView';
import TaskHistory from './components/TaskHistory';
import Settings from './components/Settings';
import ToastManager from './components/ToastManager';
import OnboardingTour from './components/OnboardingTour';
import API_URL from './config';

function App() {
  const [token, setToken] = useState(() => localStorage.getItem('auth_token') || null);
  const [username, setUsername] = useState(() => localStorage.getItem('auth_username') || '');
  const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem('auth_is_admin') === 'true');
  const [showTour, setShowTour] = useState(false);
  
  // 'board', 'dashboard', 'admin', 'calendar', 'history'
  const [currentView, setCurrentView] = useState(() => localStorage.getItem('app_current_view') || 'board');

  // Check if tour is needed whenever token changes (login or refresh)
  useEffect(() => {
    if (token) {
      fetch(`${API_URL}/profile`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      })
      .then(res => res.json())
      .then(data => {
        if (data && data.hasSeenTour === false) {
          setShowTour(true);
        }
      })
      .catch(err => console.error("Could not fetch tour preference", err));
    }
  }, [token]);

  const handleFinishTour = async () => {
    setShowTour(false);
    try {
      await fetch(`${API_URL}/profile/tour-completed`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Failed to mark tour as complete", err);
    }
  };

  // Persist current view to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('app_current_view', currentView);
  }, [currentView]);

  // Theme: 'dark', 'light', or 'auto' (follows system preference)
  const [themePref, setThemePref] = useState(() => localStorage.getItem('app_theme') || 'auto');
  const [systemTheme, setSystemTheme] = useState(() =>
    window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  );

  // Listen for OS theme changes
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Resolved theme = what's actually applied
  const resolvedTheme = themePref === 'auto' ? systemTheme : themePref;

  const cycleTheme = () => {
    const order = ['dark', 'light', 'auto'];
    const next = order[(order.indexOf(themePref) + 1) % order.length];
    setThemePref(next);
    localStorage.setItem('app_theme', next);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  const themeIcon = themePref === 'auto' ? '🔄' : resolvedTheme === 'dark' ? '☀️' : '🌙';
  const themeTitle = themePref === 'auto'
    ? `Auto (${systemTheme === 'dark' ? 'Dark' : 'Light'}) — Click for Dark`
    : themePref === 'dark'
      ? 'Dark Mode — Click for Light'
      : 'Light Mode — Click for Auto';

  const handleAuthSuccess = (newToken, newUsername, newIsAdmin) => {
    setToken(newToken);
    setUsername(newUsername);
    setIsAdmin(newIsAdmin);
    localStorage.setItem('auth_is_admin', newIsAdmin);
    setCurrentView('board');
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_username');
    localStorage.removeItem('auth_is_admin');
    localStorage.removeItem('app_current_view');
    setToken(null);
    setUsername('');
    setIsAdmin(false);
    setCurrentView('board');
    setShowTour(false);
  };

  if (!token) {
    return <AuthForm onAuthSuccess={handleAuthSuccess} />;
  }

  const renderView = () => {
    if (currentView === 'dashboard') return <Dashboard token={token} handleLogout={handleLogout} />;
    if (currentView === 'calendar') return <CalendarView token={token} handleLogout={handleLogout} />;
    if (currentView === 'history') return <TaskHistory token={token} handleLogout={handleLogout} />;
    if (currentView === 'settings') return <Settings token={token} handleLogout={handleLogout} username={username} />;
    if (currentView === 'admin' && isAdmin) return <AdminPanel token={token} handleLogout={handleLogout} username={username} />;
    return <TodoBoard token={token} handleLogout={handleLogout} />;
  };

  return (
    <div className="App" data-theme={resolvedTheme}>
      {/* Dynamic Navbar with Tabs */}
      <div className="app-navbar">
        <div className="app-navbar-left">
          <span className="app-navbar-logo">Todo-List</span>
          <div className="app-navbar-tabs">
            <button 
              className={`nav-tab ${currentView === 'board' ? 'active' : ''}`}
              onClick={() => setCurrentView('board')}
            >
              Tasks
            </button>
            <button 
              className={`nav-tab ${currentView === 'dashboard' ? 'active' : ''}`}
              onClick={() => setCurrentView('dashboard')}
            >
              Dashboard
            </button>
            <button 
              className={`nav-tab ${currentView === 'calendar' ? 'active' : ''}`}
              onClick={() => setCurrentView('calendar')}
            >
              Calendar
            </button>
            <button 
              className={`nav-tab ${currentView === 'history' ? 'active' : ''}`}
              onClick={() => setCurrentView('history')}
            >
              History
            </button>
            {isAdmin && (
              <button 
                className={`nav-tab ${currentView === 'admin' ? 'active' : ''} admin-tab`}
                onClick={() => setCurrentView('admin')}
              >
                Admin Panel 👑
              </button>
            )}
          </div>
        </div>

        <div className="app-navbar-user">
          <button
            className={`nav-tab settings-nav-tab ${currentView === 'settings' ? 'active' : ''}`}
            onClick={() => setCurrentView('settings')}
            title="Settings"
          >
            ⚙️
          </button>
          <span className="app-navbar-greeting">👤 {username}</span>
          <button
            className="theme-toggle-btn"
            onClick={cycleTheme}
            title={themeTitle}
            aria-label="Toggle theme"
          >
            {themeIcon}
          </button>
          <button className="app-logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {renderView()}
      
      {showTour && currentView === 'board' && <OnboardingTour onComplete={handleFinishTour} />}
      <ToastManager />
    </div>
  );
}

export default App;
